import { GD } from "src/GD"
import type { TUser } from "./AuthService"
import { refreshBalanceSources } from "./refreshBalanceSources"
import { getIssuerRequestContext, isIssuerRequestContextCurrent } from "./issuerRequestContext"
import { applyFreshUserBalanceOverride } from "./freshUserBalanceOverrides"
import { requestListPage } from "./listPage"

export class UsersService {
	constructor() {
		GD.REQ_USERS.listener = this.getUsers
		GD.REQ_USERS_PAGE.listener = query => {
			const requestIssuer = query.issuer ?? getIssuerRequestContext().issuer ?? undefined
			return requestListPage<IUserSaved, IUserListSummary>("users/list", query, user => applyFreshUserBalanceOverride(user, requestIssuer))
		}
		GD.REQ_GET_USER_TEMPLATE.listener = this.getUserTemplate
		GD.REQ_CREATE_USER.listener = this.createUser
		GD.REQ_GET_USER.listener = this.getUser
		GD.REQ_UPDATE_USER.listener = this.updateUser
		GD.REQ_GET_USER_ROLE.listener = this.getUserRole
		GD.REQ_SET_USER_ROLE.listener = this.setUserRole
		GD.REQ_GET_MY_PROFILE.listener = this.getMyProfile
		GD.REQ_UPDATE_MY_PROFILE.listener = this.updateMyProfile
		GD.REQ_CHANGE_MY_PASSWORD.listener = this.changeMyPassword
		GD.REQ_USER_BLOCK.listener = this.blockUser
		GD.REQ_USER_BLOCK_PREVIEW.listener = this.getUserBlockPreview
		GD.REQ_USER_BLOCK_STATUS.listener = this.getUserBlockStatus
		GD.REQ_USER_UNBLOCK.listener = this.unblockUser
		GD.REQ_USER_TOPUP_TEMPLATE.listener = this.getUserTopupTemplate
		GD.REQ_USER_CARD_ISSUE_LIMIT_TEMPLATE.listener = this.getCardIssueLimitTemplate
		GD.REQ_USER_CARD_ISSUE_LIMIT.listener = this.setCardIssueLimit

		GD.REQ_INVITE_USER.listener = this.inviteUser
		GD.REQ_INVITE_TEMPLATE.listener = this.getInviteTemplate
		GD.REQ_INVITES_LIST.listener = this.getInvites
		GD.REQ_INVITE_DELETE.listener = this.deleteInvite
		GD.REQ_INVITE_GET.listener = this.getInvite
		GD.REQ_INVITE_SIGNUP.listener = this.inviteSignup
	}

	private refreshUsers = async () => {
		const requestContext = getIssuerRequestContext()
		const usersResponse = await this.getUsers({ options: { include_teams: true } })
		if (usersResponse.data && isIssuerRequestContextCurrent(requestContext)) {
			GD.S_USERS_LIST_UPDATED.invoke(usersResponse.data)
		}
	}

	private getUserTopupTemplate: () => Promise<IAPIResponse<IUserTopUp>> = async () => {
		const res = await GD.REQ_API.request({
			method: "users/topup/get?uid=-1",
			httpMethod: "GET"
		})
		return res as IAPIResponse<IUserTopUp>
	}

	private getMyProfile: (
		options?: void | { include_teams?: boolean }
	) => Promise<IAPIResponse<TUser>> = async options => {
		return (await GD.REQ_API.request({
			method: "users/get/me",
			httpMethod: "POST",
			data: options?.include_teams ? { include_teams: "true" } : {}
		})) as IAPIResponse<TUser>
	}

	private updateMyProfile: (data: {
		name: string
	}) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const name = data.name.trim()
		if (name.length < 2 || name.length > 100) {
			return { error: { code: 2800, message: "Name must be between 2 and 100 characters" } }
		}

		const res = (await GD.REQ_API.request({
			method: "users/profile/update",
			httpMethod: "POST",
			data: { name }
		})) as IAPIResponse<IStatusResponse>

		if (res.data?.status === true) {
			GD.S_PROFILE_UPDATED.invoke({ name })
		}

		return res
	}

	private changeMyPassword: (data: {
		old_password: string
		password: string
	}) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		if (!data.old_password) {
			return { error: { code: 1, message: "Current password is required" } }
		}
		if (data.password.length < 5) {
			return { error: { code: 1, message: "New password must contain at least 5 characters" } }
		}
		if (data.password === data.old_password) {
			return { error: { code: 1, message: "New password must differ from the current password" } }
		}

		return (await GD.REQ_API.request({
			method: "users/password/change",
			httpMethod: "POST",
			data
		})) as IAPIResponse<IStatusResponse>
	}

	private getCardIssueLimitTemplate: () => Promise<
		IAPIResponse<IUserCardIssueLimit>
	> = async () => {
		const res = await GD.REQ_API.request({
			method: "users/card-issue-limit/get",
			httpMethod: "POST",
			data: { uid: "-1" }
		})

		return res as IAPIResponse<IUserCardIssueLimit>
	}

	private setCardIssueLimit: (
		data: IUserCardIssueLimit
	) => Promise<IAPIResponse<IStatusResponse>> = async (data) => {
		const res = await GD.REQ_API.request({
			method: "users/card-issue-limit",
			httpMethod: "POST",
			data
		})

		if (res?.error) return res as IAPIResponse<IStatusResponse>

		if ((res.data as IStatusResponse)?.status === true) {
			await this.refreshUsers()
			return { data: res.data as IStatusResponse }
		}

		return {
			error: { code: -1, message: "Unknown error" }
		} as IAPIResponse<IStatusResponse>
	}

	private inviteUser: (data: {
		email: string
		role?: string
		name: string
		team_uid?: string
	}) => Promise<IAPIResponse<boolean>> = async (data) => {
		const res = await GD.REQ_API.request({
			method: "users/invite",
			httpMethod: "POST",
			data
		})

		if (res.data) {
			const invites = await this.getInvites()
			if (invites.data) GD.S_INVITES_LIST_UPDATED.invoke(invites.data)
		}

		return res as IAPIResponse<boolean>
	}

	private getInviteTemplate: () => Promise<IAPIResponse<IInvite>> = async () => {
		const res = await GD.REQ_API.request({
			method: "users/invites/get?uid=-1",
			httpMethod: "GET"
		})
		return res as IAPIResponse<IInvite>
	}

	private getInvites: () => Promise<IAPIResponse<IInvite[]>> = async () => {
		return (await GD.REQ_API.request({
			method: "users/invites/list",
			httpMethod: "GET"
		})) as IAPIResponse<IInvite[]>
	}

	private deleteInvite: (data: { id: string }) => Promise<IAPIResponse<boolean>> = async (
		data
	) => {
		const res = await GD.REQ_API.request({
			method: `users/invites/delete`,
			httpMethod: "POST",
			data: { id: data.id }
		})

		const success = res.data
		if (success) {
			const invites = await this.getInvites()
			if (invites.data) GD.S_INVITES_LIST_UPDATED.invoke(invites.data)
		}

		return res as IAPIResponse<boolean>
	}

	private getInvite: (data: { code: string }) => Promise<IAPIResponse<IInvite>> = async (
		data
	) => {
		const res = await GD.REQ_API.request({
			method: `users/invites/get?token=${data.code}`,
			httpMethod: "GET"
		})
		return res as IAPIResponse<IInvite>
	}

	private inviteSignup: (data: {
		token: string
		password: string
	}) => Promise<IAPIResponse<boolean>> = async (data) => {
		const res = await GD.REQ_API.request({
			method: "users/invites/signup",
			httpMethod: "POST",
			data
		})
		return res as IAPIResponse<boolean>
	}

	private getUsers: (
		request: void | { options?: { include_teams: boolean }; issuer?: number }
	) => Promise<IAPIResponse<IUserSaved[]>> = async (request) => {
		const options = request && request.options
		const issuer = request && request.issuer
		const requestIssuer = issuer ?? getIssuerRequestContext().issuer ?? undefined
		const res = (await GD.REQ_API.request({
			httpMethod: "GET",
			method: "users/list",
			params: {
				...(options ?? {}),
				...(issuer === undefined ? {} : { issuer })
			}
		})) as IAPIResponse<IUserSaved[]>
		if (res.data) {
			res.data = res.data.map(user => applyFreshUserBalanceOverride(user, requestIssuer))
		}
		return res
	}

	private getUser: (id: string) => Promise<IAPIResponse<IUserSaved>> = async (id) => {
		const requestIssuer = getIssuerRequestContext().issuer ?? undefined
		const res = await GD.REQ_API.request({
			httpMethod: "GET",
			method: `users/get?uid=${id}&include_teams=true&include_team_list=true`
		})
		const response = res as IAPIResponse<IUserSaved>
		if (response.data) {
			response.data = applyFreshUserBalanceOverride(response.data, requestIssuer)
		}
		return response
	}

	private getUserTemplate: () => Promise<IAPIResponse<IUserRaw>> = async () => {
		const res = await GD.REQ_API.request({
			httpMethod: "GET",
			method: "users/get?uid=-1"
		})
		return res as IAPIResponse<IUserRaw>
	}

	private updateUser: (data: IUserSaved) => Promise<IAPIResponse<IStatusResponse>> =
		async (data) => {
			const editableData = Object.fromEntries(
				Object.entries(data).filter(([key]) => key !== "company_role")
			)
			const res = await GD.REQ_API.request({
				method: "users/update",
				httpMethod: "POST",
				data: editableData
			})

			if (res?.error) return res as IAPIResponse<IStatusResponse>

			if ((res.data as IStatusResponse)?.status === true) {
				await this.refreshUsers()
				return { data: res.data as IStatusResponse }
			}
			return {
				error: { code: -1, message: "Unknown error" }
			} as IAPIResponse<IStatusResponse>
		}

	private getUserRole: (data: { uid: string }) => Promise<IAPIResponse<IUserRoleForm>> =
		async (data) => {
			return (await GD.REQ_API.request({
				method: "users/role/get",
				httpMethod: "POST",
				data
			})) as IAPIResponse<IUserRoleForm>
		}

	private setUserRole: (
		data: Pick<IUserRoleForm, "uid" | "company_role">
	) => Promise<IAPIResponse<IStatusResponse>> = async (data) => {
		const res = (await GD.REQ_API.request({
			method: "users/role/set",
			httpMethod: "POST",
			data: { uid: data.uid, company_role: data.company_role }
		})) as IAPIResponse<IStatusResponse>

		if (res.error) return res
		if (res.data?.status === true) {
			await refreshBalanceSources()
			return res
		}

		return { error: { code: -1, message: "Unknown error" } }
	}

	private blockUser: (data: {
		user_uid: string
		mode: "freeze" | "block"
	}) => Promise<IAPIResponse<IUserBlockResponse>> = async (data) => {
		const res = (await GD.REQ_API.request({
			method: "users/block",
			httpMethod: "POST",
			data
		})) as IAPIResponse<IUserBlockResponse>

		if (res.error) return res

		if (res.data?.operation_uid) {
			await this.refreshUsers()
			return { data: res.data }
		}

		return { error: { code: -1, message: "Unknown error" } }
	}

	private getUserBlockPreview: (data: {
		user_uid: string
		refresh?: boolean
	}) => Promise<IAPIResponse<IUserBlockPreview>> = async data => {
		return (await GD.REQ_API.request({
			method: "users/block/preview",
			httpMethod: "POST",
			data
		})) as IAPIResponse<IUserBlockPreview>
	}

	private getUserBlockStatus: (data: {
		operation_uid: string
	}) => Promise<IAPIResponse<IUserBlockStatus>> = async (data) => {
		if (!data?.operation_uid) {
			return { error: { code: 1, message: "Operation UID is required" } }
		}

		const res = (await GD.REQ_API.request({
			method: "users/block/status",
			httpMethod: "POST",
			data
		})) as IAPIResponse<IUserBlockStatus>

		if (res.data?.status !== "pending") {
			await this.refreshUsers()
			const cardsResponse = await GD.REQ_CARDS.request({ force: true })
			if (cardsResponse.data) {
				GD.S_CARDS_LIST_UPDATED.invoke(cardsResponse.data)
			}
		}

		return res
	}

	private unblockUser: (data: {
		user_uid: string
		unfreeze_cards?: "true" | "false"
	}) => Promise<IAPIResponse<IUserUnblockResponse>> = async (data) => {
		const res = (await GD.REQ_API.request({
			method: "users/unblock",
			httpMethod: "POST",
			data
		})) as IAPIResponse<IUserUnblockResponse>

		if (res.error) return res

		if (res.data?.status === true) {
			await this.refreshUsers()
			const cardsResponse = await GD.REQ_CARDS.request({ force: true })
			if (cardsResponse.data) {
				GD.S_CARDS_LIST_UPDATED.invoke(cardsResponse.data)
			}
			return { data: res.data }
		}

		return { error: { code: -1, message: "Unknown error" } }
	}

	private createUser: (data: IUserRaw) => Promise<IAPIResponse<IUserRaw>> = async (
		data
	) => {
		const res = await GD.REQ_API.request({
			method: "users/create",
			httpMethod: "POST",
			data
		})

		if (res.error) return res
		await this.refreshUsers()

		return res as IAPIResponse<IUserSaved>
	}
}
